import { MediaImage } from '@/lib/mediaDisplay';
import { useState, useEffect, useCallback } from 'react'
import { mediaApi, ResizeMode } from '@wordpuppi/shared'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Link2, Link2Off, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

interface ImageResizeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  siteSlug: string
  mediaId: string
  currentWidth: number
  currentHeight: number
  imageUrl: string
  onResized: (newUrl: string, newWidth: number, newHeight: number, mode: ResizeMode) => void
}

export function ImageResizeDialog({
  open,
  onOpenChange,
  siteSlug,
  mediaId,
  currentWidth,
  currentHeight,
  imageUrl,
  onResized,
}: ImageResizeDialogProps) {
  const [width, setWidth] = useState(currentWidth)
  const [height, setHeight] = useState(currentHeight)
  const [lockAspectRatio, setLockAspectRatio] = useState(true)
  const [mode, setMode] = useState<ResizeMode>('create_new')
  const [resizing, setResizing] = useState(false)

  const aspectRatio = currentWidth / currentHeight

  useEffect(() => {
    if (open) {
      setWidth(currentWidth)
      setHeight(currentHeight)
      setMode('create_new')
    }
  }, [open, currentWidth, currentHeight])

  const handleWidthChange = useCallback((newWidth: number) => {
    setWidth(newWidth)
    if (lockAspectRatio && newWidth > 0) {
      setHeight(Math.round(newWidth / aspectRatio))
    }
  }, [lockAspectRatio, aspectRatio])

  const handleHeightChange = useCallback((newHeight: number) => {
    setHeight(newHeight)
    if (lockAspectRatio && newHeight > 0) {
      setWidth(Math.round(newHeight * aspectRatio))
    }
  }, [lockAspectRatio, aspectRatio])

  const handleSubmit = async () => {
    if (width <= 0 || height <= 0) {
      toast.error('Width and height must be greater than 0')
      return
    }

    if (width === currentWidth && height === currentHeight) {
      toast.error('Please enter different dimensions')
      return
    }

    setResizing(true)
    try {
      const result = await mediaApi.resize(siteSlug, mediaId, width, height, mode)
      onResized(result.data!.url, width, height, mode)
      toast.success(mode === 'replace' ? 'Image resized' : 'New image created')
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resize image')
    } finally {
      setResizing(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Resize Image</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex justify-center">
            <MediaImage
                scopeKey={siteSlug}
              src={imageUrl}
              alt="Preview"
              className="max-h-32 max-w-full rounded border border-border object-contain"
            />
          </div>

          <div className="text-sm text-muted-foreground text-center">
            Current size: {currentWidth} x {currentHeight}
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label htmlFor="resize-width">Width</Label>
              <Input
                id="resize-width"
                type="number"
                min={1}
                max={10000}
                value={width}
                onChange={(e) => handleWidthChange(parseInt(e.target.value) || 0)}
              />
            </div>

            <button
              type="button"
              onClick={() => setLockAspectRatio(!lockAspectRatio)}
              className="mb-2 p-2 rounded hover:bg-muted transition-colors"
              title={lockAspectRatio ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
            >
              {lockAspectRatio ? (
                <Link2 className="h-4 w-4" />
              ) : (
                <Link2Off className="h-4 w-4 text-muted-foreground" />
              )}
            </button>

            <div className="flex-1 space-y-1">
              <Label htmlFor="resize-height">Height</Label>
              <Input
                id="resize-height"
                type="number"
                min={1}
                max={10000}
                value={height}
                onChange={(e) => handleHeightChange(parseInt(e.target.value) || 0)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Mode</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="resize-mode"
                  value="create_new"
                  checked={mode === 'create_new'}
                  onChange={() => setMode('create_new')}
                  className="accent-primary"
                />
                <span className="text-sm">Create new image</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="resize-mode"
                  value="replace"
                  checked={mode === 'replace'}
                  onChange={() => setMode('replace')}
                  className="accent-primary"
                />
                <span className="text-sm">Replace original</span>
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              {mode === 'create_new'
                ? 'Creates a new image file. Original is kept.'
                : 'Overwrites the original image file.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={resizing}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={resizing}>
            {resizing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Resizing...
              </>
            ) : (
              'Resize'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
